import React, { useEffect, useState, useMemo } from "react";
import { auth } from "../../firebase";
import {
  LayoutGrid,
  PlusSquare,
  BookOpen,
  BarChart3,
  Menu,
  MessageCircleQuestion // New Icon for Support
} from "lucide-react";

export default function MobileHeader({ activeTab }) {
  const pageConfig = {
    overview: { title: "Portfolio", icon: LayoutGrid },
    accounts: { title: "Portfolio", icon: LayoutGrid },
    logTrades: { title: "Add Entry", icon: PlusSquare },
    addTrade: { title: "Add Entry", icon: PlusSquare },
    journal: { title: "Trade Journal", icon: BookOpen },
    performance: { title: "Dashboard", icon: BarChart3 },
    dashboard: { title: "Dashboard", icon: BarChart3 },
    support: { title: "Support", icon: MessageCircleQuestion }, // Synced ID
    more: { title: "More Options", icon: Menu }
  };

  const page = pageConfig[activeTab] || pageConfig.overview;
  const PageIcon = page.icon;

  /* ================= MARKET LOGIC ================= */
  const [utcTime, setUtcTime] = useState("");
  const [marketStats, setMarketStats] = useState({
    current: { name: "After Hours", color: "#64748b" },
    countdowns: []
  });

  useEffect(() => {
    const calculateMarketData = () => {
      const now = new Date();
      const h = now.getUTCHours();
      const m = now.getUTCMinutes();
      setUtcTime(now.toUTCString().split(' ')[4]);

      const markets = [
        { name: "Tokyo", open: 0, close: 8, color: "#10b981" },
        { name: "London", open: 8, close: 16, color: "#38BDF8" },
        { name: "New York", open: 13, close: 21, color: "#38BDF8" }
      ];

      let active = markets.find(mkt => h >= mkt.open && h < mkt.close);
      if (h >= 13 && h < 16) active = { name: "London/NY Overlap", color: "#38BDF8" };
      
      const currentSession = active 
        ? { name: active.name.toUpperCase() + " OPEN", color: active.color }
        : { name: "AFTER HOURS", color: "#64748b" };

      const countdowns = markets.map(mkt => {
        let label = (h >= mkt.open && h < mkt.close) ? `${mkt.name} Closes` : `${mkt.name} Opens`;
        let diffH = (h >= mkt.open && h < mkt.close) ? (mkt.close - h - (m > 0 ? 1 : 0)) : ((mkt.open - h + 24) % 24 - (m > 0 ? 1 : 0));
        let diffM = m > 0 ? 60 - m : 0;
        return { label, time: `${diffH}h ${diffM}m` };
      });

      setMarketStats({ current: currentSession, countdowns });
    };

    calculateMarketData();
    const interval = setInterval(calculateMarketData, 60000);
    return () => clearInterval(interval);
  }, []);

  /* ================= USER LOGIC ================= */
  const user = auth.currentUser;
  const username = user?.displayName || user?.email?.split("@")[0] || "Trader";
  const initials = username[0]?.toUpperCase() || "U";
  
  const messages = ["Protect capital first.", "Patience is your edge.", "Trade less. Execute better."];
  const dailyMessage = useMemo(() => messages[Math.floor(Math.random() * messages.length)], []);

  return (
    <div style={styles.header}>
      <div style={styles.topRow}>
        <div style={styles.left}>
          <div style={styles.iconBox}><PageIcon size={18} color="#38BDF8" /></div>
          <div style={styles.pageTitle}>{page.title}</div>
        </div>
        <div style={styles.avatar}>{initials}</div>
      </div>

      <div style={styles.center}>
        <div style={styles.greeting}>Hey, <span style={styles.username}>{username}</span> 👋</div>
        <div style={styles.motivation}>"{dailyMessage}"</div>
      </div>

      <div style={styles.marketTerminal}>
        <div style={styles.activeSession}>
          <div style={{...styles.dot, backgroundColor: marketStats.current.color}} />
          <div style={styles.sessionCol}>
            <span style={{...styles.sessionMain, color: marketStats.current.color}}>{marketStats.current.name}</span>
            <span style={styles.utcMini}>UTC {utcTime}</span>
          </div>
        </div>
        <div style={styles.vDivider} />
        <div style={styles.countdownGrid}>
          {marketStats.countdowns.map((m, i) => (
            <div key={i} style={styles.countdownItem}>
              <span style={styles.mLabel}>{m.label.split(' ')[0]}</span>
              <span style={styles.mTime}>{m.time}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const styles = {
  header: { padding: "15px 20px", background: "rgba(10, 10, 10, 0.95)", borderBottom: "1px solid rgba(255, 255, 255, 0.05)", display: "flex", flexDirection: "column", gap: "15px" },
  topRow: { display: "flex", justifyContent: "space-between", alignItems: "center" },
  left: { display: "flex", alignItems: "center", gap: 10 },
  iconBox: { width: 32, height: 32, borderRadius: 8, background: "rgba(56, 189, 248, 0.08)", display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid rgba(56, 189, 248, 0.15)" },
  pageTitle: { fontSize: 14, fontWeight: 700, color: "#f8fafc" },
  avatar: { width: 30, height: 30, borderRadius: "50%", background: "linear-gradient(135deg, #38BDF8, #0EA5E9)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 800, color: "#020617" },
  center: { textAlign: "center" },
  greeting: { fontSize: 16, fontWeight: 700, color: "#f8fafc" },
  username: { color: "#38BDF8" },
  motivation: { fontSize: 10, color: "#64748b", fontStyle: "italic", marginTop: 2 },
  marketTerminal: { display: "flex", alignItems: "center", background: "rgba(255, 255, 255, 0.02)", padding: "10px 12px", borderRadius: "12px", border: "1px solid rgba(255, 255, 255, 0.05)", justifyContent: "space-between" },
  activeSession: { display: "flex", alignItems: "center", gap: 8 },
  sessionCol: { display: "flex", flexDirection: "column" },
  dot: { width: 5, height: 5, borderRadius: "50%" },
  sessionMain: { fontSize: 9, fontWeight: 800 },
  utcMini: { fontSize: 8, color: "#64748b" },
  vDivider: { width: 1, height: 20, background: "rgba(255,255,255,0.08)" },
  countdownGrid: { display: "flex", gap: 12 },
  countdownItem: { display: "flex", flexDirection: "column", alignItems: "center" },
  mLabel: { fontSize: 8, color: "#64748b" },
  mTime: { fontSize: 9, color: "#f1f5f9" }
};